import type { ErrorEntry } from './errorRingBuffer';

export interface HumanizedError {
  summary: string;
  detail: string;
}

const RULES: Array<{ pattern: RegExp; summary: string }> = [
  {
    pattern: /dynamically imported module|importing a module script failed|chunkloaderror|loading chunk/i,
    summary: 'A part of the app failed to load (stale deployment — refresh may fix it)',
  },
  {
    pattern: /failed to fetch|networkerror|net::|load failed|err_network|err_internet/i,
    summary: 'Could not reach the server (network error)',
  },
  {
    pattern: /\b401\b|unauthorized/i,
    summary: 'The server rejected your session (401 — try signing in again)',
  },
  {
    pattern: /\b403\b|forbidden/i,
    summary: 'The server denied access to a resource (403)',
  },
  {
    pattern: /\b404\b|not found/i,
    summary: 'A requested resource was not found (404)',
  },
  {
    pattern: /\b5\d{2}\b|internal server error/i,
    summary: 'The server returned an error (5xx)',
  },
  {
    pattern: /timeout|timed out|aborterror/i,
    summary: 'A request took too long and timed out',
  },
  {
    pattern: /\bapicall\b/i,
    summary: 'An API call from this screen failed',
  },
  {
    pattern: /typeerror|referenceerror|rangeerror|undefined is not|cannot read|is not a function|null is not/i,
    summary: 'The app hit an internal error on this screen',
  },
];

export function humanizeError(entry: Pick<ErrorEntry, 'msg' | 'stack'>): HumanizedError {
  const haystack = `${entry.msg || ''} ${entry.stack || ''}`;
  const rule = RULES.find((r) => r.pattern.test(haystack));
  return {
    summary: rule ? rule.summary : 'An unexpected error was captured',
    detail: entry.msg || '',
  };
}

/**
 * Summarize the newest error-level entries into a short, de-duplicated,
 * human-readable list for display in the bug report modal.
 */
export function summarizeRecentErrors(entries: ErrorEntry[], max = 3): HumanizedError[] {
  const relevant = entries.filter((e) => e.level === 'error' || e.level === 'unhandled');
  const source = relevant.length > 0 ? relevant : entries;
  const seen = new Set<string>();
  const out: HumanizedError[] = [];
  for (let i = source.length - 1; i >= 0 && out.length < max; i--) {
    const humanized = humanizeError(source[i]);
    if (seen.has(humanized.summary)) continue;
    seen.add(humanized.summary);
    out.push(humanized);
  }
  return out;
}
